'use client';

/* eslint-disable react/jsx-no-useless-fragment */
import type { CSSProperties, JSX } from 'react';
import React, { useCallback, useMemo } from 'react';
import { IconButton2 as IconButton } from 'components/Button2';
import { ScrollContainer } from 'components/ScrollContainer';
import { useLazyLoad } from 'components/Tabs/useLazyLoad';
import { Text } from 'components/Text';
import { getBackgroundColor } from 'theme-utils';
import { isFunction } from 'utils/is-function';
import { IconArrowLeft } from '@frameio/vapor-icons';
import type { ThemeColorsBackground, ThemeSpaces } from '@frameio/vapor-theme';
import styles from './DrillDown.module.css';

type OptionalRenderCallback =
  | (() => React.ReactNode)
  | React.ReactNode
  | JSX.Element;

type BackOptions = {
  goBack: () => void;
};

type BackOptionalRenderCallback =
  | ((options: BackOptions) => React.ReactNode)
  | React.ReactNode
  | JSX.Element;

export type DrillDownColumnItem = {
  /**
   * The primary title to appear in the top navigation. Accepts
   * either a react node, or a render callback.
   */
  title?: OptionalRenderCallback;
  /**
   * The back title to ppear in the top left part of the navigation.
   * Accepts either a react node or a render callback.
   */
  back?: BackOptionalRenderCallback;
  /**
   * Optionally hide the title. Useful for things like showing the title
   * after the inline title has scrolled out of view.
   */
  titleVisible?: boolean;
  /**
   * The primary content.
   */
  content: OptionalRenderCallback;
};

type OnIndexChangeType = (index: number) => void;

export type DrillDownProps = {
  value: number;
  onChange: OnIndexChangeType;
  columns: DrillDownColumnItem[];
  /**
   * Label for the back button. Required for internationalization and accessibility.
   */
  backLabel: string;
  lazyLoad?: boolean;
  disableColumnsTitle?: boolean;
  renderPanelActions?: (
    index: number,
    onIndexChange: OnIndexChangeType
  ) => React.ReactNode;
  titleBackgroundColor?: ThemeColorsBackground;
  columnBackgroundColor?: ThemeColorsBackground;
};

/**
 * DrillDown is an iOS style navigation view, which allows
 * acts as a stack navigator.
 */

export function DrillDown({
  value,
  backLabel,
  renderPanelActions: renderPanelActionsProp,
  columns,
  lazyLoad = true,
  disableColumnsTitle = false,
  onChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  titleBackgroundColor = 'secondary',
  columnBackgroundColor = 'secondary',
}: DrillDownProps) {
  /**
   * Panel actions are interactive elements which appear in the
   * top-right side of the title area.
   */

  const panelActions = useMemo(() => {
    if (!renderPanelActionsProp) {
      return null;
    }

    const nodes = renderPanelActionsProp(value, onChange);
    return (
      <div
        className={styles.panelActions}
        style={{
          opacity: nodes ? 1 : 0,
        }}
      >
        {nodes}
      </div>
    );
  }, [onChange, renderPanelActionsProp, value]);

  const onBackClick = useCallback(() => {
    onChange(value - 1);
  }, [onChange, value]);

  const backButtonStyle = useMemo(() => {
    return getBackButtonStyle(value);
  }, [value]);

  const { shouldLoadIndex } = useLazyLoad({
    lazyLoad,
    itemCount: columns.length,
    currentPage: value,
  });

  return (
    <div className={styles.container}>
      {!disableColumnsTitle && (
        <div className={styles.titleContainer}>
          <div className={styles.backButton} style={backButtonStyle}>
            <IconButton
              icon={<IconArrowLeft />}
              label={backLabel}
              onClick={onBackClick}
              variant="tertiary"
            />
          </div>
          {/* Back description */}
          {columns.map((column, i) => {
            if (column.back) {
              const getBackLabelContents = () =>
                isFunction(column.back)
                  ? column.back({ goBack: onBackClick })
                  : column.back;
              const backLabelContents =
                shouldLoadIndex(i) && getBackLabelContents();

              return (
                <div
                  key={i}
                  className={styles.backLabelContainer}
                  style={getBackTitleStyle(i, value)}
                >
                  {backLabelContents && (
                    <>
                      {typeof backLabelContents === 'string' ? (
                        <Text color="body" variant="primary">
                          {backLabelContents}
                        </Text>
                      ) : (
                        backLabelContents
                      )}
                    </>
                  )}
                </div>
              );
            }
            return null;
          })}

          {/* The primary title */}
          {columns.map((column, i) => {
            const getTitleContents = () =>
              isFunction(column.title) ? column.title() : column.title;
            const titleContents = shouldLoadIndex(i) && getTitleContents();

            return (
              <div
                key={i}
                className={styles.primaryTitleContainer}
                style={getPrimaryTitleStyle(i, value, column.titleVisible)}
              >
                {titleContents && (
                  <>
                    {typeof titleContents === 'string' ? (
                      <Text color="body" truncate={true} variant="primary">
                        {titleContents}
                      </Text>
                    ) : (
                      titleContents
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Panel actions provided via render callback */}
          {panelActions}
        </div>
      )}

      {/* The primary content of the DrillDown, including optional
        secondary title which is not scrollable. */}
      <div className={styles.content}>
        {columns.map((item, i) => {
          return (
            <div
              key={i}
              className={styles.column}
              style={{
                ...getAnimationValue(i, value),
                backgroundColor: getBackgroundColor(columnBackgroundColor),
              }}
            >
              {shouldLoadIndex(i) ? renderFunction(item.content) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderFunction(node: OptionalRenderCallback) {
  return isFunction(node) ? node() : node;
}

type DrillDownScrollingColumnProps = {
  onScroll?: (event: React.UIEvent<HTMLDivElement, UIEvent>) => void;
  backgroundColor?: ThemeColorsBackground;
  children?: React.ReactNode;
  scrollTopPadding?: ThemeSpaces | string;
  paddingX?: ThemeSpaces;
  enableTopBorder?: boolean;
  enableTopShadow?: boolean;
};

export function DrillDownScrollingColumn({
  onScroll: onScrollProp,
  backgroundColor,
  children,
  paddingX = 0,
  enableTopBorder = true,
  enableTopShadow = true,
}: DrillDownScrollingColumnProps) {
  return (
    <ScrollContainer
      backgroundColor={backgroundColor}
      enableTopBorder={enableTopBorder}
      enableTopShadow={enableTopShadow}
      height={'100%'}
      onScroll={onScrollProp}
      paddingX={paddingX as number}
    >
      {children}
    </ScrollContainer>
  );
}

/**
 * Animations
 */

const hiddenStyles: CSSProperties = {
  visibility: 'hidden',
  opacity: 0,
};

const visibleStyles: CSSProperties = {
  visibility: 'visible',
  opacity: 1,
};

/**
 * Primary content animations
 */

function getAnimationValue(i: number, currentIndex: number) {
  // current
  if (i === currentIndex) {
    return {
      transform: `translateX(0%)`,
      ...visibleStyles,
    };
  }

  // next
  if (i > currentIndex) {
    return {
      transform: `translateX(100%)`,
      opacity: 1,
    };
  }

  // previous
  return { transform: `translateX(-50%)`, opacity: 0 };
}

/**
 * Back button animations
 */

function getBackButtonStyle(i: number): CSSProperties {
  return i === 0 ? hiddenStyles : visibleStyles;
}

/**
 * Back title animations
 */

function getBackTitleStyle(i: number, currentIndex: number) {
  // current
  if (i === currentIndex) {
    return {
      ...visibleStyles,
    };
  }

  // next
  if (i > currentIndex) {
    return { ...hiddenStyles };
  }

  // previous
  return { ...hiddenStyles };
}

/**
 * Primary title animations
 */

function getPrimaryTitleStyle(i: number, currentIndex: number, visible = true) {
  // current
  if (i === currentIndex) {
    return {
      transform: `translateX(0%)`,
      ...(visible ? visibleStyles : hiddenStyles),
    };
  }

  // next
  if (i > currentIndex) {
    return { transform: `translateX(15%)`, ...hiddenStyles };
  }

  // previous
  return { transform: `translateX(-10%)`, ...hiddenStyles };
}

